import { PenIcon } from "@xola/icons";
import { Table } from "@xola/ui-kit";
import { DemographicIcon, isEquipmentBased } from "common";
import { produce } from "immer";
import { isNil, sortBy } from "lodash";
import React, { useState } from "react";
import {
    DemographicPrice,
    DemographicPricingGroup,
    Experience,
    LineItemTemplate,
    LineItemTemplateCode,
    PricingTerm,
} from "types";
import { getDemographicPricesForExperienceCatalog } from "../../../models/DemographicPrice";
import { isDefaultDemographic } from "../../../models/DemographicUnit";
import { getEquipmentPricesForExperienceCatalog } from "../../../models/EquipmentPrice";
import { getLineItemTemplateByCode } from "../../../models/LineItemTemplate";
import { Currency } from "../../Common/Currency";
import { NoTranslate } from "../../Common/NoTranslate";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { CurrencyInput } from "../../Input/CurrencyInput";
import { EditFlatPricingModal } from "./EditFlatPricingModal";

export const FlatPricingEditor = ({
    experience,
    pricingGroup,
    onSubmit,
}: {
    experience: Experience;
    pricingGroup: DemographicPricingGroup;
    onSubmit: (terms: PricingTerm[]) => void;
}) => {
    const [showEditFlatPricingModal, setShowEditFlatPricingModal] = useState(false);

    const toggleEditFlatPricingModal = () => {
        setShowEditFlatPricingModal(!showEditFlatPricingModal);
    };

    const isEquipment = isEquipmentBased(experience);
    const demographicPrices = isEquipment
        ? getEquipmentPricesForExperienceCatalog(experience, pricingGroup.terms[0])
        : getDemographicPricesForExperienceCatalog(experience, pricingGroup.terms[0]);

    const handleSubmit = (demographicPrices: DemographicPrice[]) => {
        const flatPricingTerm = { ...pricingGroup.terms[0] };
        flatPricingTerm.demographicPrices = demographicPrices;
        onSubmit([flatPricingTerm]);
        toggleEditFlatPricingModal();
    };

    return (
        <>
            <Table>
                <Table.Head>
                    <Table.Row>
                        <Table.Header className="!text-sm">{isEquipment ? "Equipment" : "Demographics"}</Table.Header>
                        <Table.Header className="!text-sm">Price</Table.Header>
                        <Table.Header className="text-right !text-sm">Actions</Table.Header>
                    </Table.Row>
                </Table.Head>

                <Table.Body isStriped={false}>
                    {sortBy(demographicPrices, "catalogItem.sequence").map((demographicPrice) => {
                        return (
                            <Table.Row key={demographicPrice.template.id} className="justify-between">
                                <Table.Cell className="w-2/5">
                                    <NoTranslate
                                        if={!isDefaultDemographic(demographicPrice.catalogItem!.name)}
                                        className="whitespace-normal break-all"
                                    >
                                        <DemographicIcon className="mr-1" name={demographicPrice.catalogItem!.name} />
                                        {demographicPrice.catalogItem!.name}
                                    </NoTranslate>
                                </Table.Cell>
                                <Table.Cell className="w-2/5">
                                    {isNil(demographicPrice.price) ? null : (
                                        <Currency>{demographicPrice.price}</Currency>
                                    )}
                                </Table.Cell>
                                <Table.Cell className="w-1/5 text-right">
                                    <SecondaryButton
                                        className="mb-1"
                                        size="tiny"
                                        data-testid="edit-demographic-pricing-button"
                                        onClick={toggleEditFlatPricingModal}
                                    >
                                        <PenIcon />
                                    </SecondaryButton>
                                </Table.Cell>
                            </Table.Row>
                        );
                    })}
                </Table.Body>
            </Table>
            <EditFlatPricingModal
                isOpen={showEditFlatPricingModal}
                isEquipment={isEquipment}
                demographicPrices={demographicPrices}
                onClose={toggleEditFlatPricingModal}
                onSubmit={handleSubmit}
            />
        </>
    );
};

interface FlatPricingPerOutingEditorProps {
    pricingGroup: DemographicPricingGroup;
    lineItemTemplates: LineItemTemplate[];
    onChange: (terms: PricingTerm[]) => void;
}

export const FlatPricingPerOutingEditor = ({
    pricingGroup,
    lineItemTemplates,
    onChange,
}: FlatPricingPerOutingEditorProps) => {
    const fixedPriceLineItemTemplate = getLineItemTemplateByCode(
        lineItemTemplates,
        LineItemTemplateCode.PER_OUTING_PRICE,
    );

    const perOutingDemographicPriceItem = pricingGroup.terms[0].demographicPrices.find((demographicPrice) => {
        return fixedPriceLineItemTemplate && demographicPrice.template.id === fixedPriceLineItemTemplate.id;
    });

    const fixedPrice = perOutingDemographicPriceItem?.price;

    const handleChange = (value?: number) => {
        const updatedPricingGroup = produce(pricingGroup, (draft) => {
            if (fixedPriceLineItemTemplate) {
                draft.terms[0].demographicPrices = [
                    {
                        price: value,
                        template: fixedPriceLineItemTemplate,
                    },
                ];
            }
        });

        onChange([updatedPricingGroup.terms[0]]);
    };

    return <CurrencyInput value={fixedPrice} onValueChange={({ floatValue }) => handleChange(floatValue)} />;
};
